import { createContext, useCallback, useContext, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from './primitives';
import styles from './sheet.module.css';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** 'auto' fits content, 'tall' takes ~85% of the screen (comments, queue). */
  height?: 'auto' | 'tall';
  footer?: ReactNode;
}

/** Accessible bottom sheet: Esc / backdrop / swipe-down to close, focus moved inside. */
export function Sheet({ open, onClose, title, children, height = 'auto', footer }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; dy: number } | null>(null);
  const [closing, setClosing] = useState(false);

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 180);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, close]);

  if (!open) return null;

  const onPointerDown = (e: ReactPointerEvent) => {
    drag.current = { y: e.clientY, dy: 0 };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag.current || !panel.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y);
    panel.current.style.transform = `translateY(${drag.current.dy}px)`;
  };
  const onPointerUp = () => {
    if (!drag.current || !panel.current) return;
    if (drag.current.dy > 90) close();
    else panel.current.style.transform = '';
    drag.current = null;
  };

  return createPortal(
    <div className={`${styles.root} ${closing ? styles.closing : ''}`}>
      <div className={styles.backdrop} onClick={close} />
      <div ref={panel} className={`${styles.panel} ${height === 'tall' ? styles.tall : ''}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <div className={styles.handleZone} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
          <span className={styles.handle} />
        </div>
        {title && (
          <header className={styles.header}>
            <h2>{title}</h2>
            <IconButton icon="close" label="Sluiten" size={20} onClick={close} />
          </header>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ---------- app-wide sheet host ---------- */

type Render = (close: () => void) => ReactNode;
const SheetHostContext = createContext<(render: Render) => void>(() => {});

/** Lets any component open a sheet (add-to-playlist, report, share...) without owning its state. */
export function SheetHost({ children }: { children: ReactNode }) {
  const [render, setRender] = useState<Render | null>(null);
  const open = useCallback((r: Render) => setRender(() => r), []);
  const close = useCallback(() => setRender(null), []);
  return (
    <SheetHostContext.Provider value={open}>
      {children}
      {render?.(close)}
    </SheetHostContext.Provider>
  );
}

export const useOpenSheet = () => useContext(SheetHostContext);

/** A list of actions in a sheet (like an action menu). */
export function ActionList({ actions }: { actions: Array<{ label: string; icon?: ReactNode; danger?: boolean; onClick: () => void; hidden?: boolean }> }) {
  return (
    <ul className={styles.actions}>
      {actions
        .filter((a) => !a.hidden)
        .map((a) => (
          <li key={a.label}>
            <button className={a.danger ? styles.danger : undefined} onClick={a.onClick}>
              {a.icon}
              <span>{a.label}</span>
            </button>
          </li>
        ))}
    </ul>
  );
}
